import { DocumentNode } from 'graphql';
import { ApolloError, OperationVariables } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';
import isEqual from 'lodash.isequal';
import { useQuery } from '@apollo/client';
import usePrevious from 'hooks/use-previous';

export interface ScrollableQueryOptions<TVariables = OperationVariables> {
  client: any;
  defaultPageSize?: number;
  defaultSearchAttribute: Array<string>;
  defaultSortAttribute: string;
  defaultSortDescending?: boolean;
  queryVariables: TVariables;
  resultAttribute: string;
  skip?: boolean;
}

export interface ScrollableQueryPaginatorResult {
  fetchMore(): void;
}

export interface ScrollableQuerySearchingResult {
  search(value: string, options?: { disablePageSize: boolean }): void;
  value: string;
}

export interface ScrollableQueryResult<TData = any> {
  data: TData | undefined;
  error?: ApolloError;
  loading: boolean;
  pagination: ScrollableQueryPaginatorResult;
  searching: ScrollableQuerySearchingResult;
}

/**
 * Hook when rendering a list of values that should paginate with an infinite scroll. Also supports searching.
 * @param query
 * @param defaultPageSize
 * @param defaultSearchAttribute
 * @param defaultSortAttribute
 * @param defaultSortDescending
 * @param filter: Filter to apply to the query (for example should include global filter and probably more data)
 * @param resultAttribute: Attribute from result data.
 * @param skip: skip query call
 */
function useScrollableQuery<TData = any, TVariables = OperationVariables>(
  query: DocumentNode,
  {
    defaultPageSize = 25,
    defaultSearchAttribute,
    defaultSortAttribute,
    defaultSortDescending = false,
    client,
    queryVariables,
    resultAttribute,
    skip = false,
  }: ScrollableQueryOptions<TVariables>
): ScrollableQueryResult<TData> {
  // paginator
  const [currentPage, setCurrentPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [disablePageSize, setDisablePageSize] = useState(false);

  // actual query
  const { data, loading, error, fetchMore } = useQuery<TData>(query, {
    skip: skip,
    client,
    variables: {
      ...queryVariables,
      pageable: {
        page: currentPage,
        pageSize: disablePageSize ? undefined : defaultPageSize,
        ...(defaultSearchAttribute.length > 0 && { searchBy: defaultSearchAttribute[0] }),
        ...(searchValue !== '' && { search: searchValue }),
        sortedBy: defaultSortAttribute,
        sortDirection: defaultSortDescending ? 'DESC' : 'ASC',
      },
    },
  });

  const previousFilter = usePrevious(queryVariables);
  useEffect(() => {
    if (previousFilter && !isEqual(previousFilter, queryVariables)) {
      // Filter changes, restart pagination
      setCurrentPage(1);
    }
  }, [queryVariables, previousFilter]);

  const createFetchMore = (data: TData) => {
    return () => {
      fetchMore({
        variables: {
          pageable: {
            // @ts-ignore
            page: data[resultAttribute].page.currentPage + 1,
            pageSize: defaultPageSize,
            ...(searchValue !== '' && { searchBy: defaultSearchAttribute[0], search: searchValue }),
            sortedBy: defaultSortAttribute,
            sortDirection: defaultSortDescending ? 'DESC' : 'ASC',
          },
        },
        updateQuery: (prev: any, { fetchMoreResult }) => {
          if (!fetchMoreResult) {
            return prev;
          }
          let data = {};
          // @ts-ignore
          data[resultAttribute] = {
            // @ts-ignore
            ...fetchMoreResult[resultAttribute],
            // @ts-ignore
            data: [...prev[resultAttribute].data, ...fetchMoreResult[resultAttribute].data],
          };
          return data;
        },
      });
    };
  };
  // useRef to always return same instance of object (only update data, loading and error)
  const result = useRef({
    data,
    error,
    loading,
    pagination: {
      fetchMore: createFetchMore(data),
    },
    searching: {
      search: (value: string, options?: { disablePageSize: boolean }) => {
        // Restart pagination
        if (options) setDisablePageSize(options.disablePageSize);
        setCurrentPage(1);
        setSearchValue(value);
      },
      value: searchValue,
    },
  });
  result.current.data = data;
  result.current.error = error;
  result.current.loading = loading;
  result.current.pagination.fetchMore = createFetchMore(data);
  result.current.searching.value = searchValue;
  return result.current;
}

export default useScrollableQuery;
